import * as THREE from "three";
import {
  JOINT_COLORS,
  JOINT_SPHERE_SIZES,
  JOINT_SPHERE_SEGMENTS,
  JOINT_MATERIALS,
} from "./jointStyles";
import { logger } from "../../utils/logger";
import { getScalarJointReferenceValue, getScalarJointRelativeValue } from "./scalarJointValue";

const SLIDE_AXIS_RADIUS = 0.006;
const SLIDE_AXIS_MAX_RADIUS_TO_ENDPOINT_RADIUS = 0.78;
const SLIDE_SELECTION_GLOW_COLOR = JOINT_COLORS.glow;
const SLIDE_GLOW_RADIUS_SCALE = 6.0;
const SLIDE_ENDPOINT_MAX_RADIUS_TO_RANGE = 0.24;
const SLIDE_CURRENT_MARKER_RADIUS_SCALE = 1.8;
const SLIDE_CURRENT_MARKER_MIN_RADIUS = 0.02;
const SLIDE_CURRENT_MARKER_ENABLED = false;
const SLIDE_ARROW_HEAD_LENGTH = 0.075;
const SLIDE_ARROW_HEAD_RADIUS = 0.026;
const UNLIMITED_SLIDE_HALF_SPAN = 1.25;
const Y_AXIS = new THREE.Vector3(0, 1, 0);

function getSlideLimitControls(useLimits, limitControls = null) {
  if (limitControls) {
    return {
      minEnabled: limitControls.minEnabled !== false,
      maxEnabled: limitControls.maxEnabled !== false,
    };
  }
  const enabled = useLimits !== "false";
  return { minEnabled: enabled, maxEnabled: enabled };
}

function getSlideVisualRange(jointInfo, fallbackRange = [0, 1]) {
  const range =
    Array.isArray(jointInfo?.range) && jointInfo.range.length === 2
      ? jointInfo.range
      : fallbackRange;
  const controls = getSlideLimitControls(jointInfo?.useLimits, jointInfo?.limitControls);
  return [
    controls.minEnabled ? range[0] : -UNLIMITED_SLIDE_HALF_SPAN,
    controls.maxEnabled ? range[1] : UNLIMITED_SLIDE_HALF_SPAN,
  ];
}

function updateCylinderBetweenPoints(cylinder, start, end, radius = SLIDE_AXIS_RADIUS) {
  const midpoint = start.clone().add(end).multiplyScalar(0.5);
  const delta = end.clone().sub(start);
  const length = delta.length();
  const radiusScale = Math.max(1e-6, radius / SLIDE_AXIS_RADIUS);
  cylinder.position.copy(midpoint);
  cylinder.scale.set(radiusScale, Math.max(length, 1e-6), radiusScale);
  cylinder.userData.axisTubeRadius = radius;
  if (length > 1e-9) {
    cylinder.quaternion.setFromUnitVectors(Y_AXIS, delta.normalize());
  }
}

function createSlideArrowHeadGeometry() {
  const h = SLIDE_ARROW_HEAD_LENGTH;
  const r = SLIDE_ARROW_HEAD_RADIUS;
  const points = [
    new THREE.Vector2(0, -h / 2),
    new THREE.Vector2(r * 0.96, -h / 2),
    new THREE.Vector2(r * 0.92, -h * 0.32),
    new THREE.Vector2(r * 0.74, -h * 0.14),
    new THREE.Vector2(r * 0.5, h * 0.02),
    new THREE.Vector2(r * 0.32, h * 0.18),
    new THREE.Vector2(r * 0.17, h * 0.32),
    new THREE.Vector2(r * 0.07, h * 0.44),
    new THREE.Vector2(0, h / 2),
  ];
  return new THREE.LatheGeometry(points, 36);
}

function updateArrowHead(arrow, position, direction) {
  arrow.position.copy(position);
  const dir = direction.clone();
  if (dir.lengthSq() > 1e-12) {
    arrow.quaternion.setFromUnitVectors(Y_AXIS, dir.normalize());
  }
}

function markSlideRangeVisual(obj, jointUuid, jointName) {
  obj.userData.isSlideJointRangeVisual = true;
  obj.userData.jointUuid = jointUuid;
  obj.userData.jointName = jointName;
}

function getSlideEndpointRadius(baseRadius, minPos, maxPos) {
  const rangeLength = minPos.distanceTo(maxPos);
  if (!(rangeLength > 0)) return baseRadius;
  return Math.min(baseRadius, rangeLength * SLIDE_ENDPOINT_MAX_RADIUS_TO_RANGE);
}

function getSlideAxisRadius(endpointRadius) {
  return Math.min(SLIDE_AXIS_RADIUS, endpointRadius * SLIDE_AXIS_MAX_RADIUS_TO_ENDPOINT_RADIUS);
}

function getSlideGlowRadii(axisRadius, endpointRadius) {
  return Math.min(endpointRadius * 2.4, axisRadius * SLIDE_GLOW_RADIUS_SCALE);
}

/**
 * Creates visualization elements for a slide joint
 * @param {Object} jointInfo - Joint information object
 * @param {string} jointInfo.name - Joint name
 * @param {Array} jointInfo.axis - Joint axis [x, y, z]
 * @param {Array} jointInfo.range - Joint range [min, max]
 * @param {THREE.Quaternion} parentRot - Parent rotation
 * @param {boolean} showJoints - Whether to show joint visualizations
 * @param {Object} jointMarkerSizes - Joint marker sizes object
 * @returns {THREE.Group} Group containing all slide joint visualization elements
 */
export function createSlideJointVisualization(jointInfo, _parentRot, showJoints, jointMarkerSizes) {
  const { name: jointName, axis: jointAxis, uuid: jointUuid } = jointInfo;
  const jointRange = getSlideVisualRange(jointInfo);
  let limitControls = getSlideLimitControls(jointInfo?.useLimits, jointInfo?.limitControls);
  let lastShowJoints = !!showJoints;
  const referenceValue = getScalarJointReferenceValue(jointInfo);
  let currentValue = Number.isFinite(Number(jointInfo?.ref))
    ? Number(jointInfo.ref)
    : referenceValue;
  const toVisualOffset = (value) => getScalarJointRelativeValue(jointInfo, value);

  // Keep slide-joint visualization in the same local frame conventions as hinge joints.
  // The viz group lives in the body-local space, so use local axis directly.
  const localAxisViz = Array.isArray(jointAxis) && jointAxis.length === 3 ? jointAxis : [0, 0, 1];
  let worldAxis = new THREE.Vector3(...localAxisViz).normalize();
  const minPos = worldAxis.clone().multiplyScalar(toVisualOffset(jointRange[0]));
  const maxPos = worldAxis.clone().multiplyScalar(toVisualOffset(jointRange[1]));
  if (jointRange[0] === jointRange[1]) {
    logger.warn(`[SlideJointViz] Joint ${jointName} has zero-length range!`);
  }
  if (worldAxis.length() < 0.5) {
    logger.warn(`[SlideJointViz] Joint ${jointName} has a degenerate or zero axis!`);
  }
  const baseEndpointRadius = Math.max(
    1e-9,
    jointMarkerSizes ? jointMarkerSizes.endpoint : JOINT_SPHERE_SIZES.endpoint
  );
  const endpointRadius = getSlideEndpointRadius(baseEndpointRadius, minPos, maxPos);
  const endpointScale = endpointRadius / baseEndpointRadius;
  const axisRadius = getSlideAxisRadius(endpointRadius);
  const glowRadius = getSlideGlowRadii(axisRadius, endpointRadius);

  // Create a group for all joint visualization elements
  const jointVizGroup = new THREE.Group();
  jointVizGroup.userData.isJointVizGroup = true;
  jointVizGroup.userData.jointName = jointName;
  jointVizGroup.userData.jointUuid = jointUuid;
  jointVizGroup.userData.jointType = "slide";

  const glowTubeGeometry = new THREE.CylinderGeometry(SLIDE_AXIS_RADIUS, SLIDE_AXIS_RADIUS, 1, 16);
  const glowTubeMaterial = new THREE.MeshBasicMaterial({
    color: SLIDE_SELECTION_GLOW_COLOR,
    transparent: true,
    opacity: 0.24,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const glowTube = new THREE.Mesh(glowTubeGeometry, glowTubeMaterial);
  glowTube.visible = false;
  glowTube.renderOrder = 10003;
  glowTube.userData.isJointSelectionGlowOnly = true;
  glowTube.userData.jointUuid = jointUuid;
  glowTube.userData.jointName = jointName;
  updateCylinderBetweenPoints(glowTube, minPos, maxPos, glowRadius);
  jointVizGroup.add(glowTube);

  // --- Main line ---
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([minPos, maxPos]);
  const lineMaterial = new THREE.LineBasicMaterial(JOINT_MATERIALS.arc);
  const axisLine = new THREE.Line(lineGeometry, lineMaterial);
  axisLine.userData.isJointArc = true;
  axisLine.renderOrder = 1; // Render after glow
  markSlideRangeVisual(axisLine, jointUuid, jointName);
  jointVizGroup.add(axisLine);

  const axisTubeGeometry = new THREE.CylinderGeometry(SLIDE_AXIS_RADIUS, SLIDE_AXIS_RADIUS, 1, 12);
  const axisTubeMaterial = new THREE.MeshBasicMaterial({
    color: JOINT_MATERIALS.arc.color,
    transparent: true,
    opacity: 0.92,
    depthTest: false,
    depthWrite: false,
  });
  const axisTube = new THREE.Mesh(axisTubeGeometry, axisTubeMaterial);
  axisTube.userData.isJointArc = true;
  markSlideRangeVisual(axisTube, jointUuid, jointName);
  axisTube.renderOrder = 9998;
  updateCylinderBetweenPoints(axisTube, minPos, maxPos, axisRadius);
  jointVizGroup.add(axisTube);

  const arrowHeadGeometry = createSlideArrowHeadGeometry();
  const arrowHeadMaterial = new THREE.MeshBasicMaterial({
    color: JOINT_MATERIALS.arc.color,
    transparent: true,
    opacity: 0.96,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const minArrowHead = new THREE.Mesh(arrowHeadGeometry, arrowHeadMaterial);
  minArrowHead.renderOrder = 10000;
  minArrowHead.frustumCulled = false;
  minArrowHead.userData.isJointArc = true;
  markSlideRangeVisual(minArrowHead, jointUuid, jointName);
  updateArrowHead(minArrowHead, minPos, worldAxis.clone().negate());
  jointVizGroup.add(minArrowHead);

  const maxArrowHead = new THREE.Mesh(arrowHeadGeometry.clone(), arrowHeadMaterial.clone());
  maxArrowHead.renderOrder = 10000;
  maxArrowHead.frustumCulled = false;
  maxArrowHead.userData.isJointArc = true;
  markSlideRangeVisual(maxArrowHead, jointUuid, jointName);
  updateArrowHead(maxArrowHead, maxPos, worldAxis);
  jointVizGroup.add(maxArrowHead);

  // Create min/max position markers.
  const minSphereGeometry = new THREE.SphereGeometry(
    baseEndpointRadius,
    JOINT_SPHERE_SEGMENTS.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint
  );
  const minSphereMaterial = new THREE.MeshBasicMaterial({
    ...JOINT_MATERIALS.maxEndpoint,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  const minSphere = new THREE.Mesh(minSphereGeometry, minSphereMaterial);
  minSphere.position.copy(minPos);
  minSphere.scale.setScalar(endpointScale);
  minSphere.userData.isJointEndpoint = true;
  markSlideRangeVisual(minSphere, jointUuid, jointName);
  minSphere.userData.hasFiniteLimits = limitControls.minEnabled;
  minSphere.renderOrder = 10000;
  minSphere.userData.updatePosition = (min) => {
    const newMinPos = worldAxis.clone().multiplyScalar(toVisualOffset(min));
    const newMaxPos = worldAxis.clone().multiplyScalar(toVisualOffset(jointRange[1]));
    updateRangeVisuals(newMinPos, newMaxPos);
  };
  jointVizGroup.add(minSphere);

  // Create max position marker (red sphere)
  const maxSphereGeometry = new THREE.SphereGeometry(
    baseEndpointRadius,
    JOINT_SPHERE_SEGMENTS.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint
  );
  const maxSphereMaterial = new THREE.MeshBasicMaterial({
    ...JOINT_MATERIALS.maxEndpoint,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  const maxSphere = new THREE.Mesh(maxSphereGeometry, maxSphereMaterial);
  maxSphere.position.copy(maxPos);
  maxSphere.scale.setScalar(endpointScale);
  maxSphere.userData.isJointEndpoint = true;
  markSlideRangeVisual(maxSphere, jointUuid, jointName);
  maxSphere.userData.hasFiniteLimits = limitControls.maxEnabled;
  maxSphere.renderOrder = 10000;
  maxSphere.userData.updatePosition = (max) => {
    const newMaxPos = worldAxis.clone().multiplyScalar(toVisualOffset(max));
    const newMinPos = worldAxis.clone().multiplyScalar(toVisualOffset(jointRange[0]));
    updateRangeVisuals(newMinPos, newMaxPos);
  };
  jointVizGroup.add(maxSphere);

  const baseCurrentMarkerRadius =
    Math.max(
      jointMarkerSizes ? jointMarkerSizes.endpoint : JOINT_SPHERE_SIZES.endpoint,
      SLIDE_CURRENT_MARKER_MIN_RADIUS / SLIDE_CURRENT_MARKER_RADIUS_SCALE
    ) * SLIDE_CURRENT_MARKER_RADIUS_SCALE;
  const currentMarkerRadius = getSlideEndpointRadius(baseCurrentMarkerRadius, minPos, maxPos);
  const currentMarkerScale = currentMarkerRadius / baseCurrentMarkerRadius;
  const currentSphereGeometry = new THREE.SphereGeometry(
    baseCurrentMarkerRadius,
    JOINT_SPHERE_SEGMENTS.endpoint,
    JOINT_SPHERE_SEGMENTS.endpoint
  );
  const currentSphereMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const currentSphere = new THREE.Mesh(currentSphereGeometry, currentSphereMaterial);
  currentSphere.scale.setScalar(currentMarkerScale);
  currentSphere.renderOrder = 10001;
  currentSphere.frustumCulled = false;
  currentSphere.userData.isSlideCurrentMarker = true;
  markSlideRangeVisual(currentSphere, jointUuid, jointName);
  if (SLIDE_CURRENT_MARKER_ENABLED) {
    jointVizGroup.add(currentSphere);
  }

  const updateCurrentMarker = (value = currentValue) => {
    const nextValue = Number(value);
    if (Number.isFinite(nextValue)) {
      currentValue = nextValue;
    }
    currentSphere.position.copy(worldAxis.clone().multiplyScalar(toVisualOffset(currentValue)));
  };
  updateCurrentMarker(currentValue);

  function updateRangeVisuals(newMin, newMax) {
    axisLine.geometry.setFromPoints([newMin, newMax]);
    updateArrowHead(minArrowHead, newMin, worldAxis.clone().negate());
    updateArrowHead(maxArrowHead, newMax, worldAxis);
    minSphere.position.copy(newMin);
    maxSphere.position.copy(newMax);
    const nextEndpointRadius = getSlideEndpointRadius(baseEndpointRadius, newMin, newMax);
    const nextEndpointScale = nextEndpointRadius / baseEndpointRadius;
    const nextAxisRadius = getSlideAxisRadius(nextEndpointRadius);
    const nextGlowRadius = getSlideGlowRadii(nextAxisRadius, nextEndpointRadius);
    const nextCurrentRadius = getSlideEndpointRadius(baseCurrentMarkerRadius, newMin, newMax);
    updateCylinderBetweenPoints(axisTube, newMin, newMax, nextAxisRadius);
    updateCylinderBetweenPoints(glowTube, newMin, newMax, nextGlowRadius);
    minSphere.scale.setScalar(nextEndpointScale);
    maxSphere.scale.setScalar(nextEndpointScale);
    currentSphere.scale.setScalar(nextCurrentRadius / baseCurrentMarkerRadius);
    minSphere.userData.endpointRadius = nextEndpointRadius;
    maxSphere.userData.endpointRadius = nextEndpointRadius;
    currentSphere.userData.currentMarkerRadius = nextCurrentRadius;
  }
  minSphere.userData.endpointRadius = endpointRadius;
  maxSphere.userData.endpointRadius = endpointRadius;
  currentSphere.userData.currentMarkerRadius = currentMarkerRadius;

  // Store references for updates
  jointVizGroup.userData.axisLine = axisLine;
  jointVizGroup.userData.axisTube = axisTube;
  jointVizGroup.userData.minArrowHead = minArrowHead;
  jointVizGroup.userData.maxArrowHead = maxArrowHead;
  jointVizGroup.userData.minSphere = minSphere;
  jointVizGroup.userData.maxSphere = maxSphere;
  jointVizGroup.userData.currentSphere = currentSphere;
  jointVizGroup.userData.glowTube = glowTube;
  jointVizGroup.userData.worldAxis = worldAxis;
  jointVizGroup.userData.parentRot = new THREE.Quaternion();

  const applyVisibility = (visible) => {
    lastShowJoints = !!visible;
    jointVizGroup.visible = lastShowJoints;
    axisLine.visible = lastShowJoints;
    axisTube.visible = lastShowJoints;
    minSphere.visible = lastShowJoints && limitControls.minEnabled;
    maxSphere.visible = lastShowJoints && limitControls.maxEnabled;
    currentSphere.visible = SLIDE_CURRENT_MARKER_ENABLED && lastShowJoints;
    minArrowHead.visible = lastShowJoints && !limitControls.minEnabled;
    maxArrowHead.visible = lastShowJoints && !limitControls.maxEnabled;
  };

  // Update function for range and axis changes
  jointVizGroup.userData.updateAll = (
    min,
    max,
    axisLocal,
    useLimits,
    nextLimitControls = null,
    nextCurrentValue = currentValue
  ) => {
    // If a new axis is provided, recompute world axis and rebuild line endpoints
    if (Array.isArray(axisLocal) && axisLocal.length === 3) {
      const la = new THREE.Vector3(...axisLocal).normalize();
      worldAxis = la.clone();
    }
    limitControls = getSlideLimitControls(useLimits, nextLimitControls);
    const visualRange = [
      limitControls.minEnabled ? min : -UNLIMITED_SLIDE_HALF_SPAN,
      limitControls.maxEnabled ? max : UNLIMITED_SLIDE_HALF_SPAN,
    ];
    const newMin = worldAxis.clone().multiplyScalar(toVisualOffset(visualRange[0]));
    const newMax = worldAxis.clone().multiplyScalar(toVisualOffset(visualRange[1]));
    updateRangeVisuals(newMin, newMax);
    minSphere.userData.hasFiniteLimits = limitControls.minEnabled;
    maxSphere.userData.hasFiniteLimits = limitControls.maxEnabled;
    updateCurrentMarker(nextCurrentValue);
    applyVisibility(lastShowJoints);
  };
  jointVizGroup.userData.updateCurrent = updateCurrentMarker;
  jointVizGroup.userData.updateVisibility = applyVisibility;

  // Initial visibility is controlled by Scene effect via store; default to showJoints until that runs
  applyVisibility(showJoints);

  return jointVizGroup;
}

function ensurePivotForSlide(bodyGroup, initialPos, initialRot, jointPosLocal) {
  const parent = bodyGroup.parent;
  if (!parent) return null;
  if (!bodyGroup.userData._pivotGroup) {
    const pivot = new THREE.Group();
    pivot.userData.isJointPivot = true;
    parent.add(pivot);
    pivot.add(bodyGroup);
    bodyGroup.userData._pivotGroup = pivot;
  }
  const pivot = bodyGroup.userData._pivotGroup;
  const jointPos = new THREE.Vector3().fromArray(jointPosLocal || [0, 0, 0]);
  const cached = bodyGroup.userData._pivotLocal || [NaN, NaN, NaN];
  if (cached[0] !== jointPos.x || cached[1] !== jointPos.y || cached[2] !== jointPos.z) {
    // Transform joint position from body-local to parent space
    // NOTE: bodyRot is initial rotation of this body relative to parent.
    // IMPORTANT: do not use the current bodyGroup quaternion here. When physics has been
    // running, bodyGroup.quaternion can be a physics pose, which would rebuild pivots in
    // the wrong place when switching back to kinematics.
    const bodyRot = initialRot || bodyGroup.quaternion;
    const jointOffsetParent = jointPos.clone().applyQuaternion(bodyRot);
    const jointPosInParent = jointOffsetParent.clone().add(initialPos);

    // Pivot is at joint position in parent space
    pivot.position.copy(jointPosInParent);

    // Body is positioned relative to pivot so that q = 0 reproduces the
    // original pose exactly, even when the body has a non-identity rotation.
    bodyGroup.position.copy(jointOffsetParent.clone().negate());

    bodyGroup.userData._pivotLocal = [jointPos.x, jointPos.y, jointPos.z];
  }
  return pivot;
}

/**
 * Applies a slide joint value to a body group
 * @param {THREE.Group} bodyGroup - The body group to transform
 * @param {Object} joint - Joint information object
 * @param {number} value - Joint value (translation distance)
 * @param {THREE.Quaternion} initialRot - Initial rotation
 * @param {THREE.Vector3} initialPos - Initial position
 */
export function applySlideJointValue(bodyGroup, joint, value, initialRot, initialPos) {
  const appliedValue = getScalarJointRelativeValue(joint, value);
  // Match hinge-joint semantics: axis is authored in body-local coordinates, and the slide pivot
  // lives in parent-local coordinates. Transform by the body's initial rotation only.
  const localAxis = Array.isArray(joint.axis) && joint.axis.length === 3 ? joint.axis : [0, 0, 1];
  const axis = new THREE.Vector3(...localAxis).normalize().applyQuaternion(initialRot).normalize();

  if (Math.abs(appliedValue) < 1e-6) {
    // Reset to initial state - remove any existing pivot
    if (bodyGroup.userData._pivotGroup) {
      const pivot = bodyGroup.userData._pivotGroup;
      const parent = pivot.parent;
      if (parent) {
        // Move body back to parent and restore original position
        parent.add(bodyGroup);
        bodyGroup.position.copy(initialPos);
        bodyGroup.quaternion.copy(initialRot);
        // Remove pivot from scene
        parent.remove(pivot);
      }
      delete bodyGroup.userData._pivotGroup;
      delete bodyGroup.userData._pivotLocal;
    } else {
      // No pivot exists, just ensure correct positioning
      bodyGroup.position.copy(initialPos);
      bodyGroup.quaternion.copy(initialRot);
    }
    return;
  }

  // For non-zero values, create/use pivot system
  const pivot = ensurePivotForSlide(bodyGroup, initialPos, initialRot, joint.position || [0, 0, 0]);

  // Keep child's base rotation
  bodyGroup.quaternion.copy(initialRot);

  if (pivot) {
    // Pivot positioning is already handled in ensurePivotForSlide.
    // Apply translation along the already-rotated axis, starting from the
    // zero-configuration offset that keeps q = 0 identical to the original pose.
    const translation = axis.clone().multiplyScalar(appliedValue);

    // Recompute the same zero-offset we used when creating the pivot so we
    // don't depend on any intermediate bodyGroup.position changes.
    const jointPosLocal = new THREE.Vector3().fromArray(joint.position || [0, 0, 0]);
    const jointOffsetParent = jointPosLocal.clone().applyQuaternion(initialRot);
    const zeroOffset = jointOffsetParent.clone().negate();

    bodyGroup.position.copy(zeroOffset.add(translation));
  } else {
    // Fallback to original behavior
    bodyGroup.quaternion.copy(initialRot);
    bodyGroup.position.copy(initialPos);
    bodyGroup.position.add(axis.clone().multiplyScalar(appliedValue));
  }
}

/**
 * Updates the visibility of slide joint visualization elements
 * @param {THREE.Group} jointVizGroup - Joint visualization group
 * @param {boolean} showJoints - Whether to show joint visualizations
 */
export function updateSlideJointVisibility(jointVizGroup, showJoints) {
  if (!jointVizGroup) return;
  if (typeof jointVizGroup.userData.updateVisibility === "function") {
    jointVizGroup.userData.updateVisibility(showJoints);
    return;
  }

  jointVizGroup.visible = showJoints;

  if (jointVizGroup.userData.axisLine) {
    jointVizGroup.userData.axisLine.visible = showJoints;
  }
  if (jointVizGroup.userData.axisTube) {
    jointVizGroup.userData.axisTube.visible = showJoints;
  }
  if (jointVizGroup.userData.minArrowHead) {
    jointVizGroup.userData.minArrowHead.visible =
      showJoints && jointVizGroup.userData.minSphere?.userData?.hasFiniteLimits === false;
  }
  if (jointVizGroup.userData.maxArrowHead) {
    jointVizGroup.userData.maxArrowHead.visible =
      showJoints && jointVizGroup.userData.maxSphere?.userData?.hasFiniteLimits === false;
  }
  if (jointVizGroup.userData.minSphere) {
    jointVizGroup.userData.minSphere.visible =
      showJoints && jointVizGroup.userData.minSphere.userData.hasFiniteLimits !== false;
  }
  if (jointVizGroup.userData.maxSphere) {
    jointVizGroup.userData.maxSphere.visible =
      showJoints && jointVizGroup.userData.maxSphere.userData.hasFiniteLimits !== false;
  }
  if (jointVizGroup.userData.currentSphere) {
    jointVizGroup.userData.currentSphere.visible = SLIDE_CURRENT_MARKER_ENABLED && showJoints;
  }
}
